import { pointInPolygon } from './geometry';
import type { ActorFootprint, CollisionShape, Point, Polygon, RectShape } from './geometry';
import type { RegionDefinition } from './region-definition';

function squaredDistanceToRect(point: Point, rectangle: RectShape): number {
  const closestX = Math.max(rectangle.x, Math.min(point.x, rectangle.x + rectangle.w));
  const closestY = Math.max(rectangle.y, Math.min(point.y, rectangle.y + rectangle.h));
  const dx = point.x - closestX;
  const dy = point.y - closestY;
  return dx * dx + dy * dy;
}

function squaredDistanceToSegment(point: Point, start: Point, end: Point): number {
  const segmentX = end.x - start.x;
  const segmentY = end.y - start.y;
  const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY;

  if (segmentLengthSquared === 0) {
    const dx = point.x - start.x;
    const dy = point.y - start.y;
    return dx * dx + dy * dy;
  }

  const projection =
    ((point.x - start.x) * segmentX + (point.y - start.y) * segmentY) / segmentLengthSquared;
  const amount = Math.max(0, Math.min(1, projection));
  const closestX = start.x + segmentX * amount;
  const closestY = start.y + segmentY * amount;
  const dx = point.x - closestX;
  const dy = point.y - closestY;
  return dx * dx + dy * dy;
}

function circleIntersectsPolygon(center: Point, radiusSquared: number, polygon: Polygon): boolean {
  if (pointInPolygon(center, polygon)) return true;

  for (let index = 0; index < polygon.points.length; index += 1) {
    const start = polygon.points[index];
    const end = polygon.points[(index + 1) % polygon.points.length];
    if (start && end && squaredDistanceToSegment(center, start, end) < radiusSquared) return true;
  }

  return false;
}

export function circleIntersectsShape(
  center: Point,
  radius: number,
  shape: CollisionShape,
): boolean {
  const normalizedRadius = Number.isFinite(radius) && radius > 0 ? radius : 0;
  const radiusSquared = normalizedRadius * normalizedRadius;

  return shape.kind === 'rect'
    ? squaredDistanceToRect(center, shape) < radiusSquared
    : circleIntersectsPolygon(center, radiusSquared, shape);
}

function normalizedRadius(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : Number.EPSILON;
}

export function footprintIntersectsShape(
  center: Point,
  footprint: ActorFootprint,
  shape: CollisionShape,
): boolean {
  const radiusX = normalizedRadius(footprint.radiusX);
  const radiusY = normalizedRadius(footprint.radiusY);
  const normalize = (point: Point): Point => ({
    x: (point.x - center.x) / radiusX,
    y: (point.y - center.y) / radiusY,
  });

  if (shape.kind === 'rect') {
    const left = (shape.x - center.x) / radiusX;
    const right = (shape.x + shape.w - center.x) / radiusX;
    const top = (shape.y - center.y) / radiusY;
    const bottom = (shape.y + shape.h - center.y) / radiusY;
    const closestX = Math.max(left, Math.min(0, right));
    const closestY = Math.max(top, Math.min(0, bottom));
    return closestX * closestX + closestY * closestY < 1;
  }

  const normalizedPolygon: Polygon = {
    kind: 'polygon',
    id: shape.id,
    points: shape.points.map(normalize),
  };
  return circleIntersectsPolygon({ x: 0, y: 0 }, 1, normalizedPolygon);
}

export function collidesWithRegion(
  region: RegionDefinition,
  center: Point,
  radius: number,
): boolean {
  return region.collision.some((shape) => circleIntersectsShape(center, radius, shape));
}

export function footprintCollidesWithRegion(
  region: RegionDefinition,
  center: Point,
  footprint: ActorFootprint,
): boolean {
  return region.collision.some((shape) => footprintIntersectsShape(center, footprint, shape));
}
